import { createContext, type FC, type ReactNode, useContext, useEffect, useRef } from 'react';

import { VOTERS_COPY } from '../app/voters/Voters.Copy';
import { showToast } from '../components/reusable/toast';
import { type ImportVoterProps, useImportVoterProps } from '../hooks/import-voters';

const ImportVoterContext = createContext<ImportVoterProps | null>(null);

export const ImportVoterProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const value = useImportVoterProps();
	const wasProcessingRef = useRef(false);

	const { isProcessing, processed, added, rejected, heldBack, wasAborted, fatalError } = value.processingState;

	useEffect(() => {
		const wasProcessing = wasProcessingRef.current;
		wasProcessingRef.current = isProcessing;

		// Only fire on true → false transition with at least one processed record
		if (!wasProcessing || isProcessing || processed === 0) return;

		// The import keeps running when the user leaves the Import tab, so this toast is how they hear
		// it finished. The result itself stays on the Import tab — a toast is never the only record.
		const toFix = rejected + heldBack;
		if (fatalError) {
			showToast('error', `${VOTERS_COPY.import.stopped(added, toFix)}. ${fatalError}`);
		} else if (wasAborted) {
			showToast('error', VOTERS_COPY.import.stopped(added, toFix));
		} else {
			showToast(toFix > 0 ? 'error' : 'success', VOTERS_COPY.import.finished(added, toFix));
		}
	}, [isProcessing, processed, added, rejected, heldBack, wasAborted, fatalError]);

	return <ImportVoterContext.Provider value={value}>{children}</ImportVoterContext.Provider>;
};

export const useImportVoter = (): ImportVoterProps => {
	const ctx = useContext(ImportVoterContext);
	if (!ctx) {
		throw new Error('useImportVoter must be used within ImportVoterProvider');
	}
	return ctx;
};
